import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Gift, Loader2, Ticket } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const todayEt = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/New_York" });

// Buy an EYW gift card (visitors or members). locationId/enhancerId record where it was sold.
export const GiftCardPurchase = ({ locationId = "", enhancerId = "", title = "Gift card", testid = "giftcert-card" }) => {
  const { user } = useAuth();
  const { data: opts } = useQuery({ queryKey: ["gift-card-options"], queryFn: () => api.get("/gift-cards/options").then((r) => r.data) });
  const [f, setF] = useState({ amount: 100, recipient_email: "", recipient_name: "", message: "", deliver_on: "", buyer_name: "", buyer_email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const buy = async () => {
    if (!f.recipient_email) return toast.error("Add the recipient's email.");
    if (!user && (!f.buyer_name || !f.buyer_email)) return toast.error("Add your name and email for the receipt.");
    setBusy(true);
    try {
      const { data } = await api.post("/gift-certificates/purchase", {
        ...f, amount: Number(f.amount), origin_url: window.location.origin, location_id: locationId, enhancer_id: enhancerId,
        return_path: window.location.pathname,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="eyw-card p-8" data-testid={testid}>
      <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Ticket size={20} className="text-[#D98E73]" /> {title}</h3>
      <p className="text-sm text-[#4A5D6B] mt-2 mb-4">Send a gift of well-being. Their unique EYW code arrives by email, ready for any session, class, event or product across the ecosystem. Unused balance stays on the card.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs text-[#4A5D6B]">Choose an amount</label>
          <div className="flex flex-wrap gap-2 mt-1" data-testid="giftcert-denominations">
            {(opts?.denominations || []).map((d) => (
              <button key={d} type="button" onClick={() => setF({ ...f, amount: d })} data-testid={`giftcert-denom-${d}`}
                className={`rounded-full px-4 py-2 text-sm border transition-colors ${Number(f.amount) === Number(d) ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#144F75]/25 text-[#144F75] hover:bg-[#144F75]/[0.06]"}`}>{money(d)}</button>
            ))}
          </div>
          {opts?.allow_custom && (
            <div className="mt-2">
              <label className="text-xs text-[#4A5D6B]">Or a custom amount ({money(opts.min_amount)}–{money(opts.max_amount)})</label>
              <input type="number" min={opts.min_amount} max={opts.max_amount} step="0.01" value={f.amount} onChange={set("amount")} data-testid="giftcert-amount" className="eyw-input w-full" />
            </div>
          )}
        </div>
        <label className="text-xs text-[#4A5D6B]">Recipient email<input type="email" value={f.recipient_email} onChange={set("recipient_email")} data-testid="giftcert-email" className="eyw-input w-full mt-1" /></label>
        <label className="text-xs text-[#4A5D6B]">Recipient name (optional)<input value={f.recipient_name} onChange={set("recipient_name")} data-testid="giftcert-name" className="eyw-input w-full mt-1" /></label>
        <label className="text-xs text-[#4A5D6B] sm:col-span-2">Personal message (optional)<textarea rows={2} value={f.message} onChange={set("message")} data-testid="giftcert-msg" className="eyw-input w-full mt-1 resize-none" /></label>
        <label className="text-xs text-[#4A5D6B]">Send on (optional — leave blank to send now)<input type="date" min={todayEt()} value={f.deliver_on} onChange={set("deliver_on")} data-testid="giftcert-deliver-on" className="eyw-input w-full mt-1" /></label>
        {!user && <>
          <label className="text-xs text-[#4A5D6B]">Your name<input value={f.buyer_name} onChange={set("buyer_name")} data-testid="giftcert-buyer-name" className="eyw-input w-full mt-1" /></label>
          <label className="text-xs text-[#4A5D6B]">Your email (for the receipt)<input type="email" value={f.buyer_email} onChange={set("buyer_email")} data-testid="giftcert-buyer-email" className="eyw-input w-full mt-1" /></label>
        </>}
      </div>
      <button onClick={buy} disabled={busy} data-testid="giftcert-buy" className="eyw-btn w-full mt-4 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <Gift size={16} />} Purchase {money(Number(f.amount) || 0)} gift card
      </button>
      <p className="text-xs text-[#8FA89B] mt-3 text-center">Valid for one year from purchase. <Link to="/gift-card-balance" className="underline" data-testid={`${testid}-balance-link`}>Check a balance</Link></p>
    </div>
  );
};
